<script>
import Datepicker from 'vuejs-datepicker'
import VueTagsInput from '@johmun/vue-tags-input'
import store from '@state/store'
import { EVENT_MINIMUM_AGES as eventMinimumAges } from '@events/constant'
import { debounce } from 'debounce'
import { startOfMonth, endOfMonth, isSameDay } from 'date-fns'
import isArray from 'lodash/isArray'
import {
  getFiltersFromCurrentUrl,
  prepareTagsForVueTagsInput,
  setFiltersToCurrentUrl,
} from '@events/utils'

const initialFiltersState = {
  from_date: '',
  to_date: '',
  with_ongoing: true,

  availability: false,
  availability_from: '',
  availability_to: '',
  cause_ids: [],
  keyword: '',
  location: '',
  min_age: '',
  needs_leader: false,
  ongoing: false,
  tag_ids: [],
}

const initialTagsState = {
  items: [],
  input: '',
  selected: [],
}

export default {
  name: 'CompanyEventsFilters',
  components: { VueTagsInput, Datepicker },
  props: {
    companyId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      filters: { ...initialFiltersState },
      causes: { ...initialTagsState },
      tags: { ...initialTagsState },
      eventMinimumAges,
    }
  },
  computed: {
    date() {
      return {
        from_date: this.$store.state.events.filters.from_date,
        to_date: this.$store.state.events.filters.to_date,
      }
    },
    causesInput() {
      return this.causes.input
    },
    tagsInput() {
      return this.tags.input
    },
    isEligibleToClearDate() {
      return !!(
        !isSameDay(this.filters.from_date, startOfMonth(new Date())) ||
        !isSameDay(this.filters.to_date, endOfMonth(new Date())) ||
        this.filters.ongoing
      )
    },
    isEligibleToClearAvailability() {
      return !!(
        this.filters.availability_from ||
        this.filters.availability_to ||
        this.filters.needs_leader
      )
    },
  },
  watch: {
    causesInput: 'getEventCauses',
    tagsInput: 'getEventTags',
    date() {
      this.filters = { ...this.filters, ...this.date }
    },
    filters: {
      async handler() {
        const filledFilters = Object.fromEntries(
          Object.entries(this.filters).filter(([key, value]) => {
            if (isArray(value) && !value.length) return false
            return !!value
          })
        )

        setFiltersToCurrentUrl(filledFilters)

        await store.dispatch('events/fetchFilteredEvents', {
          companyId: this.companyId,
          filters: filledFilters,
        })
      },
      deep: true,
    },
  },
  created() {
    this.setFiltersFromCurrentUrl()
    this.getEventCauses()
  },
  methods: {
    setFiltersFromCurrentUrl() {
      this.filters = getFiltersFromCurrentUrl(this.filters, this.$route)

      const { cause_ids: causeIds, tag_ids: tagIds } = this.filters

      if (causeIds.length) {
        store.dispatch('events/fetchCauses', { causeIds }).then(({ data }) => {
          if (!data) return
          this.causes.selected = data.map(({ id, name }) => ({
            id,
            text: name,
          }))
        })
      }

      if (tagIds.length) {
        store.dispatch('events/fetchTags', { tagIds }).then(({ data }) => {
          if (!data) return
          this.tags.selected = data.map(({ id, name }) => ({
            id,
            text: name,
          }))
        })
      }
    },
    clearFilters() {
      this.filters = { ...initialFiltersState }
      this.causes = { ...initialTagsState }
      this.tags = { ...initialTagsState }
    },
    clearDateFilters() {
      this.filters = {
        ...this.filters,
        from_date: startOfMonth(new Date()),
        to_date: endOfMonth(new Date()),
        ongoing: false,
      }
    },
    clearAvailabilityFilters() {
      this.filters = {
        ...this.filters,
        availability_from: '',
        availability_to: '',
        needs_leader: false,
      }
    },
    clearCauseFilters() {
      this.filters = {
        ...this.filters,
        cause_ids: [],
      }
      this.causes = { ...initialTagsState }
    },
    clearTagFilters() {
      this.filters = {
        ...this.filters,
        tag_ids: [],
      }
      this.tags = { ...initialTagsState }
    },
    setFilterField: debounce(async function(e, field, minLength = 0) {
      const value = (e.target && e.target.value) || e
      this.filters[field] = value.length >= minLength ? value : ''
    }, 500),

    getEventCauses() {
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        store
          .dispatch('companies/fetchCauses')
          .then((response) => {
            this.causes.items = response.data.map((a) => {
              return { text: a.name, id: a.id }
            })
          })
          .catch(() => console.warn('Oh. Something went wrong'))
      }, 500)
    },

    updateEventCauses(newCauses) {
      this.causes.selected = newCauses
      this.filters.cause_ids = this.causes.selected.map(({ id }) => id)
    },

    getEventTags: debounce(async function getEventTags() {
      if (!this.tagsInput) return

      await store
        .dispatch('events/fetchFilteredTags', {
          startingWith: this.tagsInput,
        })
        .then(({ data }) => {
          if (!data) return
          this.tags.items = prepareTagsForVueTagsInput(data)
        })
    }, 500),

    updateEventTags(newTags) {
      this.tags.items = []
      this.tags.selected = newTags
      this.filters.tag_ids = this.tags.selected.map(({ id }) => id)
    },
  },
}
</script>

<template>
  <div class="flex justify-between my-6 px-22">
    <v-popover trigger="click" placement="bottom" :auto-hide="true">
      <div
        :class="{ 'text-blue': isEligibleToClearDate }"
        class="inline-flex items-center filter-dropdown"
      >
        <button
          v-if="isEligibleToClearDate"
          class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
          @click.prevent="clearDateFilters"
        >
          <BaseIcon name="close" />
        </button>
        <span class="mr-2 truncate">Date</span>
      </div>

      <template slot="popover">
        <div class="flex items-center mb-4.5">
          <Datepicker
            v-model="filters.from_date"
            input-class="text-center border-b border-black cursor-pointer w-26"
          />

          <span class="text-black mx-2.5 text-basel">to</span>

          <Datepicker
            v-model="filters.to_date"
            :disabled-dates="{ to: filters.from_date }"
            input-class="text-center border-b border-black cursor-pointer w-26"
          />
        </div>

        <label class="inline-flex items-center cursor-pointer">
          <input
            v-model="filters.ongoing"
            class="mr-2 checkbox"
            type="checkbox"
          />
          <span>Ongoing</span>
        </label>
      </template>
    </v-popover>

    <v-popover trigger="click" placement="bottom" :auto-hide="true">
      <div
        :class="{ 'text-blue': filters.location }"
        class="inline-flex items-center filter-dropdown"
      >
        <button
          v-if="filters.location"
          class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
          @click.prevent="setFilterField('', 'location')"
        >
          <BaseIcon name="close" />
        </button>
        <span class="mr-2 truncate">
          {{ filters.location || 'Location' }}
        </span>
      </div>

      <template slot="popover">
        <input
          class="text-center border-b border-black cursor-pointer w-58"
          placeholder="City, state, zip"
          :value="filters.location"
          @input="setFilterField($event, 'location', 2)"
        />
      </template>
    </v-popover>

    <v-popover trigger="click" placement="bottom" :auto-hide="true">
      <div
        :class="{ 'text-blue': isEligibleToClearAvailability }"
        class="inline-flex items-center filter-dropdown"
      >
        <button
          v-if="isEligibleToClearAvailability"
          class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
          @click.prevent="clearAvailabilityFilters"
        >
          <BaseIcon name="close" />
        </button>
        <span class="mr-2 truncate">Availability</span>
      </div>

      <template slot="popover">
        <div class="flex items-center ">
          <input
            :value="filters.availability_from"
            :min="0"
            :max="filters.availability_to"
            class="text-center border-b border-black cursor-pointer w-26"
            type="number"
            @input="setFilterField($event, 'availability_from', 1)"
          />

          <span class="text-black mx-2.5 text-basel">to</span>

          <input
            :value="filters.availability_to"
            :min="filters.availability_from"
            class="text-center border-b border-black cursor-pointer w-26"
            type="number"
            @input="setFilterField($event, 'availability_to', 1)"
          />
        </div>

        <span class="block text-right text-black text-sml mt-1 mb-4.5">
          (Spots available)
        </span>

        <label class="inline-flex items-center cursor-pointer">
          <input
            v-model="filters.needs_leader"
            class="mr-2 checkbox"
            type="checkbox"
          />
          <span>Events without a leader</span>
        </label>
      </template>
    </v-popover>

    <v-popover trigger="click" placement="bottom" :auto-hide="true">
      <div
        :class="{ 'text-blue': causes.selected.length }"
        class="inline-flex items-center filter-dropdown"
      >
        <button
          v-if="causes.selected.length"
          class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
          @click.prevent="clearCauseFilters"
        >
          <BaseIcon name="close" />
        </button>
        <span class="mr-2 truncate">
          {{ (causes.selected.length && causes.selected[0].text) || 'Cause' }}
        </span>
      </div>

      <template slot="popover">
        <div class="w-58">
          <VueTagsInput
            v-model="causes.input"
            :tags="causes.selected"
            :autocomplete-items="causes.items"
            :add-only-from-autocomplete="true"
            :add-on-blur="false"
            placeholder="Filter by event cause"
            :autocomplete-min-length="0"
            @tags-changed="updateEventCauses"
          />
        </div>
      </template>
    </v-popover>

    <v-popover trigger="click" placement="bottom" :auto-hide="true">
      <div
        :class="{ 'text-blue': tags.selected.length }"
        class="inline-flex items-center filter-dropdown"
      >
        <button
          v-if="tags.selected.length"
          class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
          @click.prevent="clearTagFilters"
        >
          <BaseIcon name="close" />
        </button>
        <span class="mr-2 truncate">
          {{ (tags.selected.length && tags.selected[0].text) || 'Tag' }}
        </span>
      </div>

      <template slot="popover">
        <div class="w-58">
          <VueTagsInput
            v-model="tags.input"
            :tags="tags.selected"
            :autocomplete-items="tags.items"
            :add-only-from-autocomplete="true"
            :add-on-blur="false"
            placeholder="Filter by event tag"
            @tags-changed="updateEventTags"
          />
        </div>
      </template>
    </v-popover>

    <v-popover trigger="click" placement="bottom" :auto-hide="true">
      <div
        :class="{ 'text-blue': filters.keyword }"
        class="inline-flex items-center filter-dropdown"
      >
        <button
          v-if="filters.keyword"
          class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
          @click.prevent="setFilterField('', 'keyword')"
        >
          <BaseIcon name="close" />
        </button>
        <span class="mr-2 truncate">
          {{ filters.keyword || 'Keyword' }}
        </span>
      </div>

      <template slot="popover">
        <input
          class="text-center border-b border-black cursor-pointer w-58"
          placeholder="Filter by Keyword"
          :value="filters.keyword"
          @input="setFilterField($event, 'keyword', 3)"
        />
      </template>
    </v-popover>

    <v-popover trigger="click" placement="bottom" :auto-hide="true">
      <div
        :class="{ 'text-blue': filters.min_age }"
        class="inline-flex items-center filter-dropdown"
      >
        <button
          v-if="filters.min_age"
          class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
          @click.prevent="setFilterField('', 'min_age')"
        >
          <BaseIcon name="close" />
        </button>
        <span class="mr-2 truncate">
          {{ filters.min_age || 'Age' }}
        </span>
      </div>

      <template slot="popover">
        <ul>
          <li
            v-for="{ name, value } in eventMinimumAges"
            :key="value"
            class="cursor-pointer mx-2.5 text-basel"
            :class="filters.min_age === value ? 'text-blue' : 'text-black'"
            @click="setFilterField(value, 'min_age')"
          >
            {{ name }}
          </li>
        </ul>
      </template>
    </v-popover>

    <button
      class="inline-flex items-center text-base text-left button secondary-button"
      @click="clearFilters"
    >
      <BaseIcon name="close" class="mr-1" />
      <span>Clear Filters</span>
    </button>
  </div>
</template>

<style lang="scss" scoped>
/* stylelint-disable */
.vue-tags-input::v-deep {
  .ti-input {
    @apply border-0 border-b border-black rounded-none shadow-none;
  }
  .ti-new-tag-input {
    @apply text-center text-black;
  }
}
/* stylelint-enable */
</style>
